import { memo, useState } from 'react';
import DriftEarnTable from './DriftEarnTable';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cj } from 'src/utils/classNames';
import SelectorsAndFilters from './SelectorsAndFilters';
import {
	AssetFilter,
	ProductType,
	useEarnProduct,
} from 'src/hooks/useEarnProduct';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { SelectOption } from 'src/components/primitives/Select/hooks/types';
import AboutDriftEarnSection from './AboutDriftEarn';
import { TabOption } from 'src/components/primitives/Tabs/types';
import { useTargetHeight } from 'src/hooks/useTargetHeight';
import {
	EARN_PAGE_HEAD_SECTION_ID,
	EXPLORE_COMPARE_HEAD_SECTION_ID,
} from '../constants';
import { EARN_HEAD_STICKY_Z_INDEX } from '../BorrowLend/constant';

const ExploreAndCompare = () => {
	const [searchInputValue, setSearchInputValue] = useState('');
	const [assetFilter, setAssetFilter] = useState<AssetFilter>('all');
	const [productFilter, setProductFilter] = useState<ProductType>('all');

	const isMobile = useIsMobileScreenSize();
	const isWalletConnected = useWalletIsConnected();

	const productsToRender = useEarnProduct({
		assetFilter: assetFilter,
		productFilter,
		searchInputValue,
	});

	// Product filter options
	const productFilterOptions: SelectOption<ProductType>[] = [
		{ value: 'all', label: 'All Products' },
		{ value: 'borrow_lend', label: 'Borrow/Lend' },
		{ value: 'strategy_vaults', label: 'Strategy Vaults' },
		{ value: 'insurance_fund_vaults', label: 'Insurance Fund Vaults' },
		{ value: 'amplify', label: 'Amplify' },
	];

	// Configuration-driven tabs
	const TAB_CONFIGS = [
		{
			value: 'all' as const,
			label: 'All',
			needWalletConnection: false,
			className: !isMobile ? 'min-w-[75px]' : 'min-w-[67px]',
		},
		{
			value: 'stable_coin' as const,
			label: 'Stablecoins',
			needWalletConnection: false,
			className: !isMobile ? 'min-w-[127px]' : undefined,
		},
		{
			value: 'sol_and_lst' as const,
			label: 'SOL & LSTs',
			needWalletConnection: false,
			className: !isMobile ? 'min-w-[125px]' : undefined,
		},
		{
			value: 'in_wallet' as const,
			label: 'In Wallet',
			needWalletConnection: true,
			className: !isMobile ? 'min-w-[106px]' : undefined,
		},
	];

	const tabs: TabOption<AssetFilter>[] = TAB_CONFIGS.filter(
		(config) => !config.needWalletConnection || isWalletConnected
	).map((config) => ({
		value: config.value,
		label:
			assetFilter === config.value
				? `${config.label} (${productsToRender.length})`
				: config.label,
		className: config.className,
	}));

	const topHeight = useTargetHeight({
		elementId: EARN_PAGE_HEAD_SECTION_ID,
	});

	return (
		<div className="col-span-12 grid grid-cols-subgrid gap-3">
			<div
				id={EXPLORE_COMPARE_HEAD_SECTION_ID}
				className={cj(
					'sticky col-span-12 bg-main-bg py-md',
					EARN_HEAD_STICKY_Z_INDEX
				)}
				style={{ top: `${topHeight}px` }}
			>
				<SelectorsAndFilters
					tabs={tabs}
					assetFilter={assetFilter}
					onAssetFilterChange={setAssetFilter}
					productFilter={productFilter}
					setProductFilter={setProductFilter}
					productFilterOptions={productFilterOptions}
					searchInputValue={searchInputValue}
					onSearchInputChange={setSearchInputValue}
				/>
			</div>
			<DriftEarnTable
				productsToRender={productsToRender}
				searchInputValue={searchInputValue}
				currentFilter={assetFilter}
			/>

			<AboutDriftEarnSection />
		</div>
	);
};

export default memo(ExploreAndCompare);
